import { Check, ChevronsUpDown, X } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/shared/lib/utils'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/shared/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'

export interface MultiSelectOption {
  value: string
  label: string
}

export interface MultiSelectProps {
  options: MultiSelectOption[]
  value: string[]
  onChange: (value: string[]) => void
  onBlur?: () => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  id?: string
  disabled?: boolean
  'aria-invalid'?: boolean
  /**
   * Modo compacto (para barras de filtros): en vez de un badge por selección
   * —que crece y desalinea el layout— muestra el placeholder + un badge con el
   * conteo, manteniendo el trigger en altura fija. Default: badges (sin cambios).
   */
  summary?: boolean
}

/**
 * Select múltiple con búsqueda (popover + command). Muestra los elegidos como
 * badges removibles. Agnóstico: recibe options {value,label} y un array de values.
 */
export function MultiSelect({
  options,
  value,
  onChange,
  onBlur,
  placeholder = 'Seleccione…',
  searchPlaceholder = 'Buscar…',
  emptyText = 'Sin resultados',
  id,
  disabled,
  'aria-invalid': ariaInvalid,
  summary = false
}: MultiSelectProps) {
  const [open, setOpen] = useState(false)
  const selected = options.filter((o) => value.includes(o.value))

  const toggle = (v: string) => {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v])
  }

  return (
    <Popover
      open={disabled ? false : open}
      onOpenChange={(next) => {
        if (disabled) return
        setOpen(next)
        if (!next) onBlur?.()
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          id={id}
          disabled={disabled}
          aria-expanded={open}
          aria-invalid={ariaInvalid}
          className={cn('w-full justify-between', summary ? 'h-10' : 'h-auto min-h-10')}
        >
          {summary ? (
            <span className="flex min-w-0 items-center gap-1.5">
              <span
                className={cn(
                  'truncate font-normal',
                  selected.length === 0 && 'text-muted-foreground'
                )}
              >
                {placeholder}
              </span>
              {selected.length > 0 && (
                <Badge variant="secondary" className="shrink-0 px-1.5">
                  {selected.length}
                </Badge>
              )}
            </span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {selected.length === 0 ? (
                <span className="font-normal text-muted-foreground">{placeholder}</span>
              ) : (
                selected.map((o) => (
                  <Badge key={o.value} variant="secondary" className="gap-1">
                    {o.label}
                    {!disabled && (
                      <span
                        role="button"
                        tabIndex={-1}
                        aria-label={`Quitar ${o.label}`}
                        onPointerDown={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          toggle(o.value)
                        }}
                      >
                        <X className="h-3 w-3" />
                      </span>
                    )}
                  </Badge>
                ))
              )}
            </div>
          )}
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o.value}
                  value={o.label}
                  onSelect={() => {
                    toggle(o.value)
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value.includes(o.value) ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {o.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
